import React, { useState, useCallback, useEffect } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { createBlock, DEFAULT_EMAIL_SETTINGS, getStarterBlocks } from './emailBlockRegistry';
import { serializeEmailHtml } from './emailHtmlSerializer';
import EmailBlockPalette from './EmailBlockPalette';
import EmailBlockCanvas from './EmailBlockCanvas';
import EmailBlockPropsPanel from './EmailBlockPropsPanel';
import AIAgentPanel from './AIAgentPanel';
import ContentPicker from '../ContentPicker';
import { Eye, Code, Monitor, Smartphone } from 'lucide-react';

/**
 * EmailBlockEditor — main orchestrator component.
 * Three-panel layout: palette | canvas | properties/AI
 *
 * Props:
 *   emailBlocks — initial blocks array (or null for new email)
 *   emailSettings — initial settings (or null for defaults)
 *   onChange — called with { blocks, settings, html } on every change
 *   signatures — array of signature objects for the signature block
 *   onSelectSignature — callback to open signature picker
 *   contactId, dealId, accountId — for AI context
 */
export default function EmailBlockEditor({ emailBlocks, emailSettings, existingBody, onChange, signatures = [], contactId, dealId, accountId }) {
  const [settings, setSettings] = useState(emailSettings || { ...DEFAULT_EMAIL_SETTINGS });
  const [blocks, setBlocks] = useState(() => {
    // 1. If we have saved blocks, use them
    if (emailBlocks?.length) return emailBlocks;
    // 2. If there's existing HTML body (legacy email), migrate it into a text block
    if (existingBody && existingBody.trim()) {
      return [
        createBlock('text', { content: existingBody }),
        createBlock('signature'),
      ];
    }
    // 3. Brand new email — use starter blocks
    return getStarterBlocks();
  });
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [contentPickerBlockId, setContentPickerBlockId] = useState(null);
  const [sigPickerBlockId, setSigPickerBlockId] = useState(null);

  const selectedBlock = blocks.find(b => b.block_id === selectedBlockId) || null;

  // Emit changes
  const emitChange = useCallback((newBlocks, newSettings) => {
    const s = newSettings || settings;
    const html = serializeEmailHtml(s, newBlocks);
    onChange?.({ blocks: newBlocks, settings: s, html });
  }, [settings, onChange]);

  // Block operations
  const addBlock = useCallback((blockType) => {
    const block = createBlock(blockType);
    setBlocks(prev => {
      const next = [...prev, block];
      setSelectedBlockId(block.block_id);
      setTimeout(() => emitChange(next, settings), 0);
      return next;
    });
  }, [settings, emitChange]);

  const updateBlockProps = useCallback((blockId, newProps) => {
    setBlocks(prev => {
      const next = prev.map(b => b.block_id === blockId ? { ...b, props: newProps } : b);
      emitChange(next, settings);
      return next;
    });
  }, [settings, emitChange]);

  const deleteBlock = useCallback((blockId) => {
    setBlocks(prev => {
      const next = prev.filter(b => b.block_id !== blockId);
      if (selectedBlockId === blockId) setSelectedBlockId(null);
      emitChange(next, settings);
      return next;
    });
  }, [selectedBlockId, settings, emitChange]);

  const duplicateBlock = useCallback(() => {
    if (!selectedBlock) return;
    const clone = { ...JSON.parse(JSON.stringify(selectedBlock)), block_id: 'blk_' + Math.random().toString(36).slice(2, 10) };
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.block_id === selectedBlockId);
      const next = [...prev.slice(0, idx + 1), clone, ...prev.slice(idx + 1)];
      setSelectedBlockId(clone.block_id);
      emitChange(next, settings);
      return next;
    });
  }, [selectedBlock, selectedBlockId, settings, emitChange]);

  const moveBlock = useCallback((direction) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.block_id === selectedBlockId);
      if (idx < 0) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = arrayMove(prev, idx, newIdx);
      emitChange(next, settings);
      return next;
    });
  }, [selectedBlockId, settings, emitChange]);

  const reorderBlocks = useCallback((oldIndex, newIndex) => {
    setBlocks(prev => {
      const next = arrayMove(prev, oldIndex, newIndex);
      emitChange(next, settings);
      return next;
    });
  }, [settings, emitChange]);

  // Content library image selection
  const handleContentPickerSelect = useCallback((asset) => {
    if (contentPickerBlockId && asset?.url) {
      updateBlockProps(contentPickerBlockId, {
        ...(blocks.find(b => b.block_id === contentPickerBlockId)?.props || {}),
        image_url: asset.url,
        alt_text: asset.name || '',
      });
    }
    setContentPickerBlockId(null);
  }, [contentPickerBlockId, blocks, updateBlockProps]);

  // Signature selection
  const handleSelectSignature = useCallback((sig) => {
    if (sigPickerBlockId && sig) {
      updateBlockProps(sigPickerBlockId, {
        ...(blocks.find(b => b.block_id === sigPickerBlockId)?.props || {}),
        signature_id: sig.signature_id,
        signature_html: sig.content || '',
      });
    }
    setSigPickerBlockId(null);
  }, [sigPickerBlockId, blocks, updateBlockProps]);

  // AI content update
  const handleAIUpdateContent = useCallback((blockId, html) => {
    const block = blocks.find(b => b.block_id === blockId);
    if (block?.block_type === 'text') {
      updateBlockProps(blockId, { ...block.props, content: html });
    }
  }, [blocks, updateBlockProps]);

  // AI image insert
  const handleAIInsertImage = useCallback((url, alt) => {
    const block = createBlock('image');
    block.props.image_url = url;
    block.props.alt_text = alt || '';
    setBlocks(prev => {
      const insertIdx = selectedBlockId ? prev.findIndex(b => b.block_id === selectedBlockId) + 1 : prev.length;
      const next = [...prev.slice(0, insertIdx), block, ...prev.slice(insertIdx)];
      setSelectedBlockId(block.block_id);
      emitChange(next, settings);
      return next;
    });
  }, [selectedBlockId, settings, emitChange]);

  // Preview HTML
  const previewHtml = showPreview ? serializeEmailHtml(settings, blocks) : '';

  return (
    <div className="flex h-full">
      {/* Left: Block Palette */}
      <EmailBlockPalette onAddBlock={addBlock} />

      {/* Center: Canvas or Preview */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-stone-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-1">
            <button onClick={() => setShowPreview(false)}
              className={`px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors ${!showPreview ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-700'}`}>
              Edit
            </button>
            <button onClick={() => setShowPreview(true)}
              className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors ${showPreview ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-700'}`}>
              <Eye className="w-3 h-3" /> Preview
            </button>
          </div>
          {showPreview && (
            <div className="flex items-center gap-1">
              <button onClick={() => setPreviewMode('desktop')} className={`p-1 rounded ${previewMode === 'desktop' ? 'bg-stone-100 text-stone-700' : 'text-stone-400'}`}>
                <Monitor className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setPreviewMode('mobile')} className={`p-1 rounded ${previewMode === 'mobile' ? 'bg-stone-100 text-stone-700' : 'text-stone-400'}`}>
                <Smartphone className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {showPreview ? (
          <div className="flex-1 overflow-auto bg-stone-100 p-4 flex justify-center">
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="bg-white rounded shadow-sm border-0"
              style={{ width: previewMode === 'mobile' ? '375px' : `${settings.max_width || 600}px`, maxWidth: '100%', height: '100%', minHeight: '400px' }}
            />
          </div>
        ) : (
          <EmailBlockCanvas
            blocks={blocks}
            selectedBlockId={selectedBlockId}
            settings={settings}
            onSelectBlock={setSelectedBlockId}
            onUpdateBlockProps={updateBlockProps}
            onDeleteBlock={deleteBlock}
            onReorderBlocks={reorderBlocks}
            onOpenContentPicker={(blockId) => setContentPickerBlockId(blockId)}
            onSelectSignature={(blockId) => setSigPickerBlockId(blockId)}
          />
        )}
      </div>

      {/* Right: Properties / AI */}
      <div className="w-[260px] flex-shrink-0 border-l border-stone-200 bg-white flex flex-col overflow-hidden">
        {showAIPanel ? (
          <AIAgentPanel
            selectedBlock={selectedBlock}
            allBlocks={blocks}
            onUpdateBlockContent={handleAIUpdateContent}
            onInsertImageBlock={handleAIInsertImage}
            contactId={contactId}
            dealId={dealId}
            accountId={accountId}
          />
        ) : (
          <EmailBlockPropsPanel
            block={selectedBlock}
            onChange={(newProps) => selectedBlock && updateBlockProps(selectedBlock.block_id, newProps)}
            onDelete={() => selectedBlock && deleteBlock(selectedBlock.block_id)}
            onDuplicate={duplicateBlock}
            onMoveUp={() => moveBlock('up')}
            onMoveDown={() => moveBlock('down')}
            showAIPanel={showAIPanel}
            onToggleAI={() => setShowAIPanel(!showAIPanel)}
          />
        )}
        {showAIPanel && (
          <div className="px-3 py-2 border-t border-stone-200">
            <button onClick={() => setShowAIPanel(false)} className="w-full text-xs text-stone-500 hover:text-stone-700 underline">
              Back to Properties
            </button>
          </div>
        )}
      </div>

      {/* Content Library Picker */}
      <ContentPicker
        open={!!contentPickerBlockId}
        onOpenChange={(open) => { if (!open) setContentPickerBlockId(null); }}
        onSelect={handleContentPickerSelect}
        allowedTypes={['image']}
        mode="insert"
      />

      {/* Signature Picker */}
      {sigPickerBlockId && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setSigPickerBlockId(null)}>
          <div className="bg-white rounded-xl shadow-xl border border-stone-200 p-4 w-80 max-h-96 overflow-y-auto" onClick={e => e.stopPropagation()}>
            <p className="text-sm font-semibold text-stone-700 mb-3">Choose Signature</p>
            {signatures.length === 0 && <p className="text-xs text-stone-400">No signatures available</p>}
            {signatures.map(sig => (
              <button key={sig.signature_id} onClick={() => handleSelectSignature(sig)}
                className="w-full text-left px-3 py-2 text-xs text-stone-700 hover:bg-stone-50 rounded-lg border border-transparent hover:border-stone-200 mb-1">
                {sig.is_default && <span className="text-amber-500 mr-1">★</span>}
                {sig.name}
                {sig.is_admin_template && <span className="ml-1 text-[9px] text-stone-400">(Template)</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
